import { isApiRecord, type ApiValue } from '../../api/responseDecoders'

// Billing domain types + the wire decoder for GET /billing/catalog.
//
// The API speaks snake_case; the console speaks camelCase. That mapping is
// explicit and validated here rather than being assumed via `apiRequest<T>`'s
// generic, which is a compile-time claim about an untrusted payload, not a
// runtime check.

/** Money as integer minor units. Matches the portable checkout `CheckoutMoney`. */
export interface Money {
  amountMinor: number
  currency: string
}

export interface CreditPack {
  id: string
  credits: number
  price: Money
  /** Saving vs the entry tier's rate, whole percent. 0 for the entry tier. */
  discountPercent: number
  recommended: boolean
}

function isFiniteInt(value: ApiValue): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value)
}

/**
 * Decode one wire pack, or throw.
 *
 * Validated field by field rather than trusting `apiRequest<T>`'s generic — a
 * generic is a compile-time claim about an untrusted payload, not a check.
 * Money and credits must be INTEGERS: a float here would mean the backend
 * changed its money representation, and silently accepting that is how a
 * rounding bug reaches a price label.
 */
function decodePack(raw: ApiValue, index: number): CreditPack {
  const where = `billing catalog pack #${index}`
  if (!isApiRecord(raw)) {
    throw new Error(`${where}: expected an object`)
  }
  const { id, credits, amount_minor: amountMinor, currency, discount_percent: discount } = raw
  if (typeof id !== 'string' || id === '') {
    throw new Error(`${where}: missing id`)
  }
  if (!isFiniteInt(credits) || credits <= 0) {
    throw new Error(`${where} (${id}): credits must be a positive integer`)
  }
  if (!isFiniteInt(amountMinor) || amountMinor < 0) {
    throw new Error(`${where} (${id}): amount_minor must be a non-negative integer`)
  }
  if (typeof currency !== 'string' || currency === '') {
    throw new Error(`${where} (${id}): missing currency`)
  }
  if (!isFiniteInt(discount) || discount < 0 || discount > 100) {
    throw new Error(`${where} (${id}): discount_percent must be 0-100`)
  }
  // Required by the contract, so validate rather than default. `x === true`
  // would silently turn an absent or wrong-typed field into `false`, which
  // accepts a malformed catalog as if it were valid data.
  if (typeof raw.recommended !== 'boolean') {
    throw new Error(`${where} (${id}): recommended must be a boolean`)
  }
  return {
    id,
    credits,
    price: { amountMinor, currency },
    discountPercent: discount,
    recommended: raw.recommended,
  }
}

export function decodeCatalog(payload: ApiValue): CreditPack[] {
  if (!isApiRecord(payload) || !Array.isArray(payload.packs)) {
    throw new Error('billing catalog: expected a `packs` array')
  }
  return payload.packs.map(decodePack)
}

/** A sandbox order. Mirrors what a real order record would carry. */
export interface SandboxOrder {
  orderId: string
  packId: string
  credits: number
  price: Money
  /** ISO-8601. */
  createdAt: string
  status: 'pending' | 'paid'
  /** Set once placed. */
  paidAt: string | null
}

/**
 * Minor-unit exponent for a currency: 2 for USD, 0 for KRW/JPY, 3 for BHD.
 *
 * Resolved from `Intl` rather than a hand-kept table, so the browser's own ISO
 * 4217 data is the source of truth. An unrecognized code falls back to 2, which
 * is right for the long tail.
 */
function minorUnitExponent(currency: string, locale: string): number {
  try {
    const resolved = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).resolvedOptions()
    return resolved.maximumFractionDigits ?? 2
  } catch {
    return 2
  }
}

/**
 * Format minor units for display, e.g. 5000 USD -> "$50.00".
 *
 * **Not every currency has two decimals.** The live catalog returns whatever
 * DCP resolved for the buyer, so this has to divide by the currency's own
 * exponent: a flat /100 renders ₩13,000 as ₩130 and 9.999 BHD as 99.990 BHD.
 * The backend emits true ISO minor units (see `Money.to_minor_units`), and this
 * is the matching half of that contract.
 *
 * `locale` is required from UI callers — defaulting to en-US would render US
 * grouping and currency placement inside the Korean and Vietnamese bundles.
 */
export function formatMoney(money: Money, locale = 'en-US'): string {
  const exponent = minorUnitExponent(money.currency, locale)
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: money.currency,
  }).format(money.amountMinor / 10 ** exponent)
}

/** Format a credit count, e.g. 2000 -> "2,000". */
export function formatCredits(credits: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(credits)
}


// ---------------------------------------------------------------------------
// Server-backed checkout (RFC pending; issues #3451/#3452)
// ---------------------------------------------------------------------------
//
// These replace the browser-local sandbox order. The important difference is
// not that the data now comes from an API — it is that the ORDER ID is minted
// by DCP rather than by us, so it is the id every later step joins on: the
// return from Meta's card page, the status poll, and eventually fulfilment.

/** Whether a checkout will take real money. */
export type PaymentContainerMode = 'TEST' | 'LIVE'

/**
 * What `POST /billing/checkout/handoff` returns.
 *
 * `handoffCode` is a single-use bearer credential for the Meta-hosted checkout
 * host. It is carried in memory only — never localStorage, never the URL, never
 * a log line — which is why the checkout page receives it through router state
 * rather than re-fetching it.
 */
export interface CheckoutHandoff {
  orderId: string
  handoffCode: string
  expiresAt: number
  bootstrapUrl: string
  price: Money
  credits: number
  paymentContainerMode: PaymentContainerMode
}

/** Our own record of an order, from `GET /billing/checkout/orders/{id}`. */
export interface CheckoutOrder {
  orderId: string
  status: string
  credits: number
  price: Money | null
  paymentContainerMode: PaymentContainerMode | null
  createdAt: string
}

function isContainerMode(value: ApiValue): value is PaymentContainerMode {
  return value === 'TEST' || value === 'LIVE'
}

/** Decode a handoff, or throw. Validated field by field for the same reason
 * `decodePack` is: a generic is a compile-time claim about an untrusted body. */
export function decodeHandoff(raw: ApiValue): CheckoutHandoff {
  if (!isApiRecord(raw)) throw new Error('checkout handoff: expected an object')
  const {
    order_id: orderId,
    handoff_code: handoffCode,
    expires_at: expiresAt,
    bootstrap_url: bootstrapUrl,
    amount_minor: amountMinor,
    currency,
    credits,
    payment_container_mode: mode,
  } = raw
  if (typeof orderId !== 'string' || orderId === '') {
    throw new Error('checkout handoff: missing order_id')
  }
  if (typeof handoffCode !== 'string' || handoffCode === '') {
    throw new Error('checkout handoff: missing handoff_code')
  }
  if (!isFiniteInt(expiresAt)) throw new Error('checkout handoff: expires_at must be an integer')
  // Only an https destination is acceptable: this is where a bearer credential
  // gets POSTed, so a downgraded or relative URL is a credential leak.
  if (typeof bootstrapUrl !== 'string' || !bootstrapUrl.startsWith('https://')) {
    throw new Error('checkout handoff: bootstrap_url must be an https URL')
  }
  if (!isFiniteInt(amountMinor) || amountMinor < 0) {
    throw new Error('checkout handoff: amount_minor must be a non-negative integer')
  }
  if (typeof currency !== 'string' || currency === '') {
    throw new Error('checkout handoff: missing currency')
  }
  if (!isFiniteInt(credits) || credits <= 0) {
    throw new Error('checkout handoff: credits must be a positive integer')
  }
  if (!isContainerMode(mode)) {
    // Never default this. Guessing LIVE risks presenting a real charge as a
    // test; guessing TEST mislabels a real one.
    throw new Error(`checkout handoff: unknown payment_container_mode ${String(mode)}`)
  }
  return {
    orderId,
    handoffCode,
    expiresAt,
    bootstrapUrl,
    price: { amountMinor, currency },
    credits,
    paymentContainerMode: mode,
  }
}

/** Decode our order record, or throw. */
export function decodeCheckoutOrder(raw: ApiValue): CheckoutOrder {
  if (!isApiRecord(raw)) throw new Error('checkout order: expected an object')
  const {
    order_id: orderId,
    status,
    credits,
    amount_minor: amountMinor,
    currency,
    payment_container_mode: mode,
    created_at: createdAt,
  } = raw
  if (typeof orderId !== 'string' || orderId === '') {
    throw new Error('checkout order: missing order_id')
  }
  if (typeof status !== 'string' || status === '') throw new Error('checkout order: missing status')
  if (!isFiniteInt(credits) || credits <= 0) {
    throw new Error('checkout order: credits must be a positive integer')
  }
  if (typeof createdAt !== 'string' || createdAt === '') {
    throw new Error('checkout order: missing created_at')
  }
  // Price is absent until DCP quotes it, so null is legitimate here — but a
  // half-present price is not: the two arrive together or not at all.
  //
  // Written as a widening if/else rather than a ternary over two booleans so
  // the narrowing is real. `as` is forbidden in this codebase, and it would be
  // exactly the wrong tool here: asserting the type of a field whose type is
  // the thing in question.
  let price: Money | null = null
  const hasCurrency = typeof currency === 'string' && currency !== ''
  if (isFiniteInt(amountMinor) && hasCurrency) {
    // Same floor as decodeHandoff. A negative total is not a price, and letting
    // one through here would render a minus sign on a purchase screen.
    if (amountMinor < 0) {
      throw new Error(`checkout order: amount_minor must be non-negative: ${amountMinor}`)
    }
    price = { amountMinor, currency }
  } else if (isFiniteInt(amountMinor) || hasCurrency) {
    throw new Error('checkout order: amount_minor and currency must arrive together')
  }
  // Absent is legitimate (the order has not been quoted yet). Present-but-
  // unrecognised is not: nulling it would make malformed data indistinguishable
  // from an unquoted order, and the thing being suppressed is the label that
  // says whether this was a real charge.
  if (mode !== undefined && mode !== null && !isContainerMode(mode)) {
    throw new Error(`checkout order: unknown payment_container_mode ${String(mode)}`)
  }
  return {
    orderId,
    status,
    credits,
    price,
    paymentContainerMode: isContainerMode(mode) ? mode : null,
    createdAt,
  }
}
